// Voice text into the composer (BUILD 2 C2-8): the path for an utterance that
// is not sent — a guard kept it (G2, G3), the person chose Edit, or a pending
// turn became a draft (G6, G8, an exit). The text goes in after the draft's
// existing text with BUILD 1's spacing rule (ui/speech/interactive/insert.ts),
// and it is never sent from here.
//
// One of the two places an utterance's text is read (`toChatText()`, pinned
// by test/voice-no-keyword-routing.mjs); the other is ./turn-body.ts.
import { insertTranscript } from '@/speech/interactive/insert';
import type { Utterance } from './contracts';

export type ComposerLike = {
  getState(): { text: string };
  setText(text: string): void;
};

/** Insert the utterance at the end of the draft; false when nothing changed. */
export function insertUtterance(composer: ComposerLike, utterance: Utterance): boolean {
  const current = composer.getState().text;
  const end = { start: current.length, end: current.length };
  const next = insertTranscript(current, end, utterance.toChatText());
  if (next.text === current) return false;
  composer.setText(next.text);
  return true;
}

/**
 * Drafts left behind when a thread closed with a turn pending (G8): kept in
 * memory for that thread only, and inserted when its composer is back. Sign-out
 * clears them.
 */
const drafts = new Map<string, Utterance[]>();

export function keepDraft(threadId: string, utterance: Utterance): void {
  drafts.set(threadId, [...(drafts.get(threadId) ?? []), utterance]);
}

/** Insert and forget the thread's kept drafts; the count inserted. */
export function restoreDrafts(threadId: string, composer: ComposerLike): number {
  const kept = drafts.get(threadId) ?? [];
  drafts.delete(threadId);
  return kept.filter((utterance) => insertUtterance(composer, utterance)).length;
}

export function clearDrafts(): void {
  drafts.clear();
}
